import { ButtonBase, Divider, IconButton, Menu, Typography } from '@mui/material';
import { MOMENTS_COACH_FAQS_URL } from '@shared/constants';
import type { User } from '@shared/types';
import type { FC, MouseEvent } from 'react';
import { useRef, useState } from 'react';
import Avatar from '../Avatar';
import SideBarAvatarMenuItem from '../SideBarAvatarMenuItem';

interface SideBarAvatarMenuProps {
  variant?: 'icon' | 'expanded';
  menuMode?: 'default' | 'sign-out-only';
  onAccountSettings: () => void;
  onFeedBack: () => void;
  onSignOut: () => void;
  user: User | null;
  isAdmin?: boolean;
  /** On admin routes the role item becomes "Switch to User View" instead of "Admin Area". */
  isAdminRoute?: boolean;
  lightTheme?: boolean;
}

const SideBarAvatarMenu: FC<SideBarAvatarMenuProps> = ({
  variant = 'icon',
  menuMode = 'default',
  user = null,
  onSignOut,
  onFeedBack: _onFeedBack,
  onAccountSettings: _onAccountSettings,
  isAdmin = false,
  isAdminRoute = false,
  lightTheme = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const avatarButtonRef = useRef<HTMLButtonElement | null>(null);

  const onOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const onClose = () => {
    avatarButtonRef.current?.focus();
    setAnchorEl(null);
  };

  if (!user) return null;

  const isSignOutOnly = menuMode === 'sign-out-only';

  return (
    <>
      {variant === 'expanded' ? (
        <ButtonBase
          aria-controls={anchorEl ? 'avatar-menu' : undefined}
          aria-expanded={anchorEl ? 'true' : undefined}
          aria-haspopup="true"
          className={`flex w-full items-center gap-3 rounded-2xl px-2 py-1 text-left ${lightTheme ? 'hover:bg-black/5 focus-visible:bg-black/5' : 'hover:bg-white/10 focus-visible:bg-white/10'}`}
          id="avatar-menu-button"
          onClick={onOpen}
          ref={avatarButtonRef}
        >
          <Avatar
            firstName={user.firstName}
            lastName={user.lastName}
            loading={!user}
            src={user.avatarUrl ?? ''}
          />
          <div className="flex min-w-0 flex-col">
            <Typography
              className={lightTheme ? 'truncate text-cement-600' : 'truncate text-white'}
              sx={{ fontWeight: 700 }}
              variant="body2"
            >
              {user.firstName}
            </Typography>
            <Typography
              className={lightTheme ? 'truncate text-cement-400' : 'truncate text-white'}
              variant="caption"
            >
              {isSignOutOnly ? 'Account' : 'Profile and Settings'}
            </Typography>
          </div>
        </ButtonBase>
      ) : (
        <IconButton
          aria-controls={anchorEl ? 'avatar-menu' : undefined}
          aria-expanded={anchorEl ? 'true' : undefined}
          aria-haspopup="true"
          className={
            lightTheme
              ? 'cursor-pointer ring-2 ring-cement-300'
              : 'cursor-pointer ring-2 ring-white'
          }
          id="avatar-menu-button"
          onClick={onOpen}
          ref={avatarButtonRef}
          size="small"
          sx={{ p: 0 }}
        >
          <Avatar
            firstName={user.firstName}
            lastName={user.lastName}
            loading={!user}
            src={user.avatarUrl ?? ''}
          />
        </IconButton>
      )}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        className="ml-4"
        id="avatar-menu"
        open={!!anchorEl}
        PaperProps={{
          sx: {
            mt: 1.5, // give a little breathing room from the avatar
          },
        }}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        MenuListProps={{
          'aria-labelledby': 'avatar-menu-button',
        }}
        onClose={onClose}
      >
        <div className="flex w-64 flex-col items-center border-0 border-b border-solid border-cement-100 px-4 pb-4 pt-3 text-center gap-y-2">
          <Avatar
            firstName={user.firstName}
            lastName={user.lastName}
            size="l"
            src={user.avatarUrl ?? ''}
          />
          <Typography variant="subtitle1">{user.firstName}</Typography>
          <Typography className="pii" variant="body2">
            {user.email}
          </Typography>
        </div>
        {!isSignOutOnly && (
          <SideBarAvatarMenuItem
            icon="settings"
            label="Settings"
            route="/settings/integrations"
            onClick={onClose}
          />
        )}
        {!isSignOutOnly && (
          <SideBarAvatarMenuItem
            icon="support"
            label="Moments FAQs"
            endIcon="open_in_new"
            href={MOMENTS_COACH_FAQS_URL}
            target="_blank"
            rel="noreferrer"
            onClick={onClose}
          />
        )}
        {!isSignOutOnly && isAdmin && <Divider />}
        {!isSignOutOnly && isAdmin && !isAdminRoute && (
          <SideBarAvatarMenuItem
            icon="admin_panel_settings"
            label="Admin Area"
            route="/admin"
            onClick={onClose}
          />
        )}
        {!isSignOutOnly && isAdmin && isAdminRoute && (
          <SideBarAvatarMenuItem
            icon="arrow_back"
            label="Switch to User View"
            route="/members"
            onClick={onClose}
          />
        )}
        <Divider />
        <SideBarAvatarMenuItem
          icon="logout"
          label="Sign out"
          onClick={() => {
            onSignOut();
            onClose();
          }}
        />
      </Menu>
    </>
  );
};

export default SideBarAvatarMenu;
